/**
 * AdminExerciseLibrary - Biblioteca Maestra de Ejercicios GOATGYM
 * 
 * Panel Admin para gestionar ejercicios con:
 * - Videos integrados (sin YouTube externo)
 * - Anatomía visual / músculos trabajados
 * - Explicación técnica, errores comunes, consejos
 * - Filtros por categoría, músculo, equipamiento, nivel
 * - Subida de videos e imágenes anatómicas
 * - Preparado para migración a S3
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Dumbbell,
  Search,
  Loader2,
  Play,
  Image,
  X,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle2,
  Clock,
  Filter,
  Video,
  Upload,
  Eye,
  RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import VideoUploader from "../../components/VideoUploader";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Colores por categoría
const CATEGORY_COLORS = {
  pierna: "#22C55E",
  gluteo: "#EC4899",
  pecho: "#3B82F6",
  espalda: "#8B5CF6",
  hombro: "#F59E0B",
  brazo: "#EF4444",
  core: "#06B6D4",
  cardio: "#10B981",
  fullbody: "#D4AF37",
};

const AdminExerciseLibrary = () => {
  const { getAuthHeaders } = useAuth();
  
  // States
  const [exercises, setExercises] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [muscleFilter, setMuscleFilter] = useState("all");
  const [equipmentFilter, setEquipmentFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  
  // Preview
  const [previewExercise, setPreviewExercise] = useState(null);
  
  // Form Data
  const [formData, setFormData] = useState({
    name: "",
    primary_muscle: "",
    secondary_muscles: [],
    equipment: "",
    category: "",
    level: "principiante",
    video_url: "",
    anatomy_image: "",
    technique_explanation: "",
    common_errors: [],
    execution_tips: [],
    suggested_sets: "",
    suggested_reps: "",
    suggested_rest: "",
    notes: "",
  });
  
  // Temp inputs for arrays
  const [newError, setNewError] = useState("");
  const [newTip, setNewTip] = useState("");

  // Fetch data
  useEffect(() => {
    fetchMetadata();
    fetchExercises();
  }, []);

  const fetchMetadata = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/exercise-library/metadata`, {
        headers: getAuthHeaders(),
      });
      setMetadata(response.data);
    } catch (error) {
      console.error("Error fetching metadata:", error);
    }
  };

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/exercise-library`, {
        headers: getAuthHeaders(),
        params: { include_inactive: true }
      });
      setExercises(response.data.exercises || []);
      setStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching exercises:", error);
      toast.error("Error al cargar ejercicios");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      primary_muscle: "",
      secondary_muscles: [],
      equipment: "",
      category: "",
      level: "principiante",
      video_url: "",
      anatomy_image: "",
      technique_explanation: "",
      common_errors: [],
      execution_tips: [],
      suggested_sets: "",
      suggested_reps: "",
      suggested_rest: "",
      notes: "",
    });
    setEditingExercise(null);
    setActiveTab("basic");
    setNewError("");
    setNewTip("");
  };

  const openEditDialog = (exercise) => {
    setEditingExercise(exercise);
    setFormData({
      name: exercise.name || "",
      primary_muscle: exercise.primary_muscle || "",
      secondary_muscles: exercise.secondary_muscles || [],
      equipment: exercise.equipment || "",
      category: exercise.category || "",
      level: exercise.level || "principiante",
      video_url: exercise.video_url || "",
      anatomy_image: exercise.anatomy_image || "",
      technique_explanation: exercise.technique_explanation || "",
      common_errors: exercise.common_errors || [],
      execution_tips: exercise.execution_tips || [],
      suggested_sets: exercise.suggested_sets || "",
      suggested_reps: exercise.suggested_reps || "",
      suggested_rest: exercise.suggested_rest?.toString() || "",
      notes: exercise.notes || "",
    });
    setDialogOpen(true);
    setActiveTab("basic");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.primary_muscle || !formData.equipment || !formData.category) {
      toast.error("Por favor completa los campos obligatorios");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        suggested_rest: formData.suggested_rest ? parseInt(formData.suggested_rest) : null,
      };
      
      if (editingExercise) {
        await axios.put(
          `${API_URL}/api/admin/exercise-library/${editingExercise.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Ejercicio actualizado");
      } else {
        await axios.post(
          `${API_URL}/api/admin/exercise-library`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Ejercicio creado");
      }
      
      fetchExercises();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving exercise:", error);
      toast.error(error.response?.data?.detail || "Error al guardar el ejercicio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (exerciseId) => {
    if (!window.confirm("¿Desactivar este ejercicio? (Se puede reactivar después)")) return;
    
    setDeleting(exerciseId);
    try {
      await axios.delete(`${API_URL}/api/admin/exercise-library/${exerciseId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Ejercicio desactivado");
      fetchExercises();
    } catch (error) {
      console.error("Error deleting exercise:", error);
      toast.error("Error al desactivar el ejercicio");
    } finally {
      setDeleting(null);
    }
  };

  const handleToggle = async (exerciseId) => {
    try {
      await axios.patch(`${API_URL}/api/admin/exercise-library/${exerciseId}/toggle`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success("Estado actualizado");
      fetchExercises();
    } catch (error) {
      console.error("Error toggling exercise:", error);
      toast.error("Error al cambiar estado");
    }
  };

  const addCommonError = () => {
    if (newError.trim()) {
      setFormData({
        ...formData,
        common_errors: [...formData.common_errors, newError.trim()]
      });
      setNewError("");
    }
  };

  const removeCommonError = (index) => {
    setFormData({
      ...formData,
      common_errors: formData.common_errors.filter((_, i) => i !== index)
    });
  };

  const addExecutionTip = () => {
    if (newTip.trim()) {
      setFormData({
        ...formData,
        execution_tips: [...formData.execution_tips, newTip.trim()]
      });
      setNewTip("");
    }
  };

  const removeExecutionTip = (index) => {
    setFormData({
      ...formData,
      execution_tips: formData.execution_tips.filter((_, i) => i !== index)
    });
  };

  const toggleSecondaryMuscle = (muscleId) => {
    const current = formData.secondary_muscles;
    if (current.includes(muscleId)) {
      setFormData({
        ...formData,
        secondary_muscles: current.filter(m => m !== muscleId)
      });
    } else {
      setFormData({
        ...formData,
        secondary_muscles: [...current, muscleId]
      });
    }
  };

  // Filter exercises
  const filteredExercises = exercises.filter((ex) => {
    const matchesSearch = ex.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ex.primary_muscle?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === "all" || ex.category === categoryFilter;
    const matchesMuscle = muscleFilter === "all" || ex.primary_muscle === muscleFilter;
    const matchesEquipment = equipmentFilter === "all" || ex.equipment === equipmentFilter;
    const matchesLevel = levelFilter === "all" || ex.level === levelFilter;
    return matchesSearch && matchesCategory && matchesMuscle && matchesEquipment && matchesLevel;
  });

  const activeFiltersCount = [categoryFilter, muscleFilter, equipmentFilter, levelFilter]
    .filter(f => f !== "all").length;

  // Get video status badge
  const getVideoStatusBadge = (exercise) => {
    if (exercise.video_url && !exercise.video_url.includes("youtube")) {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle2 className="w-3 h-3 mr-1" />Video</Badge>;
    }
    if (exercise.video_status === "coming_soon") {
      return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" />Próximamente</Badge>;
    }
    return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Sin video</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-exercise-library">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-white">Biblioteca de Ejercicios</h1>
          <p className="text-muted-foreground mt-1 text-sm">Gestiona ejercicios con videos, técnica y anatomía</p>
        </div>
        
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold shadow-lg shadow-[#D4AF37]/20" 
          data-testid="add-exercise-button"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuevo Ejercicio
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                </div>
                <Dumbbell className="w-8 h-8 text-[#D4AF37]/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Con Video</p>
                  <p className="text-2xl font-bold text-green-400">{stats.with_video}</p>
                </div>
                <Video className="w-8 h-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Próximamente</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.coming_soon}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Categorías</p>
                  <p className="text-2xl font-bold text-[#D4AF37]">{Object.keys(stats.by_category || {}).length}</p>
                </div>
                <Filter className="w-8 h-8 text-[#D4AF37]/50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar ejercicios..."
              className="pl-10 bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
              data-testid="search-exercises-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              "border-[#2A2A2A] text-white hover:bg-[#1A1A1A]",
              activeFiltersCount > 0 && "border-[#D4AF37] text-[#D4AF37]"
            )}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtros {activeFiltersCount > 0 && `(${activeFiltersCount})`}
            {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
          </Button>
        </div>

        {/* Filter dropdowns */}
        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A]">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Categoría</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="bg-[#121212] border-[#2A2A2A] text-white h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                  <SelectItem value="all" className="text-white">Todas</SelectItem>
                  {metadata?.categories?.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id} className="text-white">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
                        {cat.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Músculo</Label>
              <Select value={muscleFilter} onValueChange={setMuscleFilter}>
                <SelectTrigger className="bg-[#121212] border-[#2A2A2A] text-white h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                  <SelectItem value="all" className="text-white">Todos</SelectItem>
                  {metadata?.muscle_groups?.map((mg) => (
                    <SelectItem key={mg.id} value={mg.id} className="text-white">{mg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Equipamiento</Label>
              <Select value={equipmentFilter} onValueChange={setEquipmentFilter}>
                <SelectTrigger className="bg-[#121212] border-[#2A2A2A] text-white h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                  <SelectItem value="all" className="text-white">Todos</SelectItem>
                  {metadata?.equipment?.map((eq) => (
                    <SelectItem key={eq.id} value={eq.id} className="text-white">{eq.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Nivel</Label>
              <Select value={levelFilter} onValueChange={setLevelFilter}>
                <SelectTrigger className="bg-[#121212] border-[#2A2A2A] text-white h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                  <SelectItem value="all" className="text-white">Todos</SelectItem>
                  {metadata?.levels?.map((lvl) => (
                    <SelectItem key={lvl.id} value={lvl.id} className="text-white">{lvl.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        {filteredExercises.length} ejercicios encontrados
      </div>

      {/* Exercise Grid */}
      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37] mx-auto" />
          <p className="text-muted-foreground mt-2">Cargando biblioteca...</p>
        </div>
      ) : filteredExercises.length === 0 ? (
        <div className="p-8 text-center">
          <Dumbbell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No se encontraron ejercicios</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredExercises.map((exercise) => (
            <Card 
              key={exercise.id} 
              className={cn(
                "bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#D4AF37]/50 transition-all cursor-pointer group",
                !exercise.is_active && "opacity-50"
              )}
              onClick={() => setPreviewExercise(exercise)}
              data-testid={`exercise-card-${exercise.id}`}
            >
              <CardContent className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white truncate group-hover:text-[#D4AF37] transition-colors">
                      {exercise.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge 
                        className="text-xs border" 
                        style={{ 
                          backgroundColor: `${CATEGORY_COLORS[exercise.category]}20`,
                          borderColor: `${CATEGORY_COLORS[exercise.category]}50`,
                          color: CATEGORY_COLORS[exercise.category]
                        }}
                      >
                        {exercise.category}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {metadata?.muscle_groups?.find(m => m.id === exercise.primary_muscle)?.name || exercise.primary_muscle}
                      </span>
                    </div>
                  </div>
                  {getVideoStatusBadge(exercise)}
                </div>

                {/* Equipment & Level */}
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                  <span>{metadata?.equipment?.find(e => e.id === exercise.equipment)?.name || exercise.equipment}</span>
                  <span>•</span>
                  <span className="capitalize">{exercise.level}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-[#2A2A2A]">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setPreviewExercise(exercise); }}
                    className="flex-1 h-8 text-xs hover:bg-[#2A2A2A]"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Ver
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); openEditDialog(exercise); }}
                    className="flex-1 h-8 text-xs hover:bg-[#2A2A2A]"
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); handleToggle(exercise.id); }}
                    className="h-8 w-8 hover:bg-[#2A2A2A]"
                    title={exercise.is_active ? "Desactivar" : "Activar"}
                  >
                    {exercise.is_active ? (
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                    ) : (
                      <RotateCcw className="w-4 h-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl">
              {editingExercise ? "Editar Ejercicio" : "Nuevo Ejercicio"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4 bg-[#1A1A1A]">
                <TabsTrigger value="basic" className="text-xs">Básico</TabsTrigger>
                <TabsTrigger value="technique" className="text-xs">Técnica</TabsTrigger>
                <TabsTrigger value="media" className="text-xs">Media</TabsTrigger>
                <TabsTrigger value="config" className="text-xs">Config</TabsTrigger>
              </TabsList>

              {/* Basic Tab */}
              <TabsContent value="basic" className="space-y-4 mt-4">
                <div>
                  <Label className="text-white">Nombre *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white mt-1"
                    placeholder="Ej: Prensa de Piernas 45°"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Categoría *</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white mt-1">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                        {metadata?.categories?.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id} className="text-white">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
                              {cat.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white">Nivel *</Label>
                    <Select value={formData.level} onValueChange={(v) => setFormData({ ...formData, level: v })}>
                      <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                        {metadata?.levels?.map((lvl) => (
                          <SelectItem key={lvl.id} value={lvl.id} className="text-white">{lvl.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Músculo Principal *</Label>
                    <Select value={formData.primary_muscle} onValueChange={(v) => setFormData({ ...formData, primary_muscle: v })}>
                      <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white mt-1">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                        {metadata?.muscle_groups?.map((mg) => (
                          <SelectItem key={mg.id} value={mg.id} className="text-white">{mg.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white">Equipamiento *</Label>
                    <Select value={formData.equipment} onValueChange={(v) => setFormData({ ...formData, equipment: v })}>
                      <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white mt-1">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                        {metadata?.equipment?.map((eq) => (
                          <SelectItem key={eq.id} value={eq.id} className="text-white">{eq.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-white mb-2 block">Músculos Secundarios</Label>
                  <div className="flex flex-wrap gap-2">
                    {metadata?.muscle_groups?.filter(m => m.id !== formData.primary_muscle).map((mg) => (
                      <Badge
                        key={mg.id}
                        variant="outline"
                        className={cn(
                          "cursor-pointer transition-colors",
                          formData.secondary_muscles.includes(mg.id)
                            ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                            : "border-[#2A2A2A] text-muted-foreground hover:border-[#D4AF37]/50"
                        )}
                        onClick={() => toggleSecondaryMuscle(mg.id)}
                      >
                        {mg.name}
                        {formData.secondary_muscles.includes(mg.id) && <X className="w-3 h-3 ml-1" />}
                      </Badge>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Technique Tab */}
              <TabsContent value="technique" className="space-y-4 mt-4">
                <div>
                  <Label className="text-white">Explicación Técnica</Label>
                  <Textarea
                    value={formData.technique_explanation}
                    onChange={(e) => setFormData({ ...formData, technique_explanation: e.target.value })}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white mt-1 min-h-[120px]"
                    placeholder="Describe la técnica correcta paso a paso..."
                  />
                </div>

                <div>
                  <Label className="text-white mb-2 block">Errores Comunes</Label>
                  <div className="space-y-2">
                    {formData.common_errors.map((error, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg p-2">
                        <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                        <span className="text-sm text-white flex-1">{error}</span>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeCommonError(idx)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input
                        value={newError}
                        onChange={(e) => setNewError(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCommonError())}
                        className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                        placeholder="Añadir error común..."
                      />
                      <Button type="button" variant="outline" onClick={addCommonError} className="border-[#2A2A2A]">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-white mb-2 block">Consejos de Ejecución</Label>
                  <div className="space-y-2">
                    {formData.execution_tips.map((tip, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-lg p-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span className="text-sm text-white flex-1">{tip}</span>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeExecutionTip(idx)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input
                        value={newTip}
                        onChange={(e) => setNewTip(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExecutionTip())}
                        className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                        placeholder="Añadir consejo..."
                      />
                      <Button type="button" variant="outline" onClick={addExecutionTip} className="border-[#2A2A2A]">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Media Tab */}
              <TabsContent value="media" className="space-y-4 mt-4">
                {/* Video Upload Section */}
                {editingExercise && (
                  <div>
                    <Label className="text-white mb-2 block">Video de Técnica</Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Sube un video corto (10-30s) mostrando la técnica correcta. Formato vertical 9:16 recomendado.
                    </p>
                    <VideoUploader
                      exerciseId={editingExercise.id}
                      currentVideoUrl={editingExercise.video_url && !editingExercise.video_url.includes("youtube") ? editingExercise.video_url : null}
                      currentThumbnail={editingExercise.video_thumbnail}
                      getAuthHeaders={getAuthHeaders}
                      onUploadComplete={(data) => {
                        setEditingExercise({
                          ...editingExercise,
                          video_url: data.video_url,
                          video_thumbnail: data.thumbnail_url,
                          video_poster: data.poster_url,
                          video_status: "available"
                        });
                        setFormData({
                          ...formData,
                          video_url: data.video_url
                        });
                        fetchExercises();
                      }}
                      onVideoDelete={async () => {
                        try {
                          const response = await axios.delete(`${API_URL}/api/cloudinary/video`, {
                            headers: getAuthHeaders(),
                            data: {
                              exercise_id: editingExercise.id,
                              public_id: editingExercise.video_public_id
                            }
                          });
                          setEditingExercise({
                            ...editingExercise,
                            video_url: null,
                            video_thumbnail: null,
                            video_poster: null,
                            video_public_id: null,
                            video_status: "coming_soon"
                          });
                          setFormData({
                            ...formData,
                            video_url: ""
                          });
                          toast.success("Video eliminado");
                          fetchExercises();
                        } catch (error) {
                          console.error("Error deleting video:", error);
                          toast.error("Error al eliminar el video");
                        }
                      }}
                    />
                  </div>
                )}

                {/* Manual URL input for new exercises */}
                {!editingExercise && (
                  <div>
                    <Label className="text-white">URL de Video (opcional)</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Puedes añadir el video después de crear el ejercicio.
                    </p>
                    <Input
                      value={formData.video_url}
                      onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                      className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                      placeholder="URL del video (se puede añadir después)"
                    />
                  </div>
                )}

                {/* Legacy YouTube warning */}
                {editingExercise?.video_url?.includes("youtube") && (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-400">Video de YouTube detectado</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Este ejercicio tiene un enlace de YouTube. Sube un video propio para reemplazarlo y mejorar la experiencia móvil.
                        </p>
                        <p className="text-xs text-muted-foreground mt-2 font-mono bg-[#1A1A1A] p-2 rounded truncate">
                          {editingExercise.video_url}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-white">Imagen Anatómica / Músculos</Label>
                  <p className="text-xs text-muted-foreground mb-2">URL de imagen mostrando los músculos trabajados.</p>
                  <Input
                    value={formData.anatomy_image}
                    onChange={(e) => setFormData({ ...formData, anatomy_image: e.target.value })}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                    placeholder="https://cdn.goatgym.app/anatomy/ejercicio.png"
                  />
                </div>
              </TabsContent>

              {/* Config Tab */}
              <TabsContent value="config" className="space-y-4 mt-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-white">Series Sugeridas</Label>
                    <Input
                      value={formData.suggested_sets}
                      onChange={(e) => setFormData({ ...formData, suggested_sets: e.target.value })}
                      className="bg-[#1A1A1A] border-[#2A2A2A] text-white mt-1"
                      placeholder="3-4"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Reps Sugeridas</Label>
                    <Input
                      value={formData.suggested_reps}
                      onChange={(e) => setFormData({ ...formData, suggested_reps: e.target.value })}
                      className="bg-[#1A1A1A] border-[#2A2A2A] text-white mt-1"
                      placeholder="10-12"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Descanso (seg)</Label>
                    <Input
                      type="number"
                      value={formData.suggested_rest}
                      onChange={(e) => setFormData({ ...formData, suggested_rest: e.target.value })}
                      className="bg-[#1A1A1A] border-[#2A2A2A] text-white mt-1"
                      placeholder="90"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white">Notas Adicionales</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white mt-1"
                    placeholder="Notas para el usuario o el entrenador..."
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex gap-3 pt-4 border-t border-[#2A2A2A]">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingExercise ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewExercise} onOpenChange={() => setPreviewExercise(null)}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] max-w-2xl max-h-[90vh] overflow-y-auto">
          {previewExercise && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white font-heading text-xl flex items-center gap-3">
                  {previewExercise.name}
                  <Badge 
                    style={{ 
                      backgroundColor: `${CATEGORY_COLORS[previewExercise.category]}20`,
                      borderColor: `${CATEGORY_COLORS[previewExercise.category]}50`,
                      color: CATEGORY_COLORS[previewExercise.category]
                    }}
                  >
                    {previewExercise.category}
                  </Badge>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* Video Placeholder */}
                <div className="aspect-video bg-[#1A1A1A] rounded-lg flex items-center justify-center border border-[#2A2A2A]">
                  {previewExercise.video_url && !previewExercise.video_url.includes("youtube") ? (
                    <video 
                      src={previewExercise.video_url} 
                      controls 
                      className="w-full h-full rounded-lg"
                    />
                  ) : (
                    <div className="text-center p-8">
                      <Video className="w-16 h-16 text-[#D4AF37]/30 mx-auto mb-4" />
                      <p className="text-[#D4AF37] font-medium">Video Próximamente</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Video animado profesional en desarrollo
                      </p>
                    </div>
                  )}
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-[#1A1A1A] p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Músculo Principal</p>
                    <p className="text-white font-medium">{metadata?.muscle_groups?.find(m => m.id === previewExercise.primary_muscle)?.name}</p>
                  </div>
                  <div className="bg-[#1A1A1A] p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Equipamiento</p>
                    <p className="text-white font-medium">{metadata?.equipment?.find(e => e.id === previewExercise.equipment)?.name}</p>
                  </div>
                  <div className="bg-[#1A1A1A] p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Nivel</p>
                    <p className="text-white font-medium capitalize">{previewExercise.level}</p>
                  </div>
                  <div className="bg-[#1A1A1A] p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Series × Reps</p>
                    <p className="text-white font-medium">
                      {previewExercise.suggested_sets || "3-4"} × {previewExercise.suggested_reps || "10-12"}
                    </p>
                  </div>
                </div>

                {/* Secondary Muscles */}
                {previewExercise.secondary_muscles?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Músculos Secundarios</p>
                    <div className="flex flex-wrap gap-2">
                      {previewExercise.secondary_muscles.map((m) => (
                        <Badge key={m} variant="outline" className="border-[#2A2A2A] text-white">
                          {metadata?.muscle_groups?.find(mg => mg.id === m)?.name || m}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Technique */}
                {previewExercise.technique_explanation && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Técnica</p>
                    <p className="text-white text-sm bg-[#1A1A1A] p-3 rounded-lg">
                      {previewExercise.technique_explanation}
                    </p>
                  </div>
                )}

                {/* Common Errors */}
                {previewExercise.common_errors?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Errores Comunes</p>
                    <div className="space-y-2">
                      {previewExercise.common_errors.map((error, idx) => (
                        <div key={idx} className="flex items-start gap-2 bg-red-500/10 p-2 rounded-lg">
                          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-white">{error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Execution Tips */}
                {previewExercise.execution_tips?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Consejos de Ejecución</p>
                    <div className="space-y-2">
                      {previewExercise.execution_tips.map((tip, idx) => (
                        <div key={idx} className="flex items-start gap-2 bg-green-500/10 p-2 rounded-lg">
                          <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-white">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <Button
                  onClick={() => { setPreviewExercise(null); openEditDialog(previewExercise); }}
                  className="w-full bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Editar Ejercicio
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminExerciseLibrary;
